import React from 'react'
import { useLocation, useNavigate } from 'react-router'
import { ApplicationScreeningDecision } from '_gql'
import { yupResolver } from '@hookform/resolvers/yup'
import { useLDClient } from 'launchdarkly-react-client-sdk'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { FormProvider, useForm } from '@divvy-web/skylab.react-hook-form'
import { TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'

import { getEmailHashFromStorage, useAuth } from '@sputnik/auth'
import BillBrandLogo from '@sputnik/components/BillBrandLogo'
import { useExperimentTracking } from '@sputnik/experiments'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import { GoodFitSurveyValidationSchema, goodFitSurveyValidationSchema } from './goodFitSurveyValidationSchema'
import GoodFitWizardForm from './GoodFitWizardForm'
import { useScreenForCreditApplication } from './ScreenForCreditApplication.gql'

const failedGoodFitFlowExperimentFlagName = 'beep-credit-application-failed-good-fit-flow'

const GoodFitSurveyPage = () => {
  const ldClient = useLDClient()
  const { track: trackExperiment } = useExperimentTracking()
  const [makeTestId] = useNameFormatter('GoodFitSurveyPage')
  const navigate = useNavigate()
  const { email } = useAuth()
  const showToast = useToast()
  const { search } = useLocation()
  const { track } = useWebTracking()

  const methods = useForm<GoodFitSurveyValidationSchema>({
    defaultValues: {
      annualRevenue: '',
      currentCash: '',
      creditRange: '',
      __currentStep: 1,
      __totalStepCount: 3,
    },
    resolver: yupResolver(goodFitSurveyValidationSchema),
  })
  const { handleSubmit, reset } = methods

  const [screenForCreditApplication, { loading: isScreeningForCreditApplication }] = useScreenForCreditApplication({
    onCompleted: (data) => {
      const isCleared = data?.screenForCreditApplication?.decision === ApplicationScreeningDecision.CLEARED

      const logCreditApplicationScreeningResult = (path: string) => {
        logInfo('screenForCreditApplication', {
          attributes: {
            isCleared,
            decision: data?.screenForCreditApplication?.decision,
            result: `Sending user to path ${path}`,
          },
          eventName: 'screenForCreditApplicationSuccess',
        })
      }

      const navigateToGoodFit = () => {
        logCreditApplicationScreeningResult(PATHNAME_CONSTS.GOOD_FIT_RESULT_PATH)
        navigate({ pathname: PATHNAME_CONSTS.GOOD_FIT_RESULT_PATH, search }, { replace: true })
      }

      const navigateToNotGoodFit = () => {
        logCreditApplicationScreeningResult(PATHNAME_CONSTS.NOT_GOOD_FIT_RESULT_PATH)
        navigate(
          {
            pathname: PATHNAME_CONSTS.NOT_GOOD_FIT_RESULT_PATH,
            search,
          },
          {
            state: {
              dsaVersion: data?.screenForCreditApplication?.dsaVersion,
            },
            replace: true,
          },
        )
      }

      if (isCleared) {
        navigateToGoodFit()
        return
      }

      if (!ldClient) {
        navigateToNotGoodFit()
        return
      }

      const emailHash = getEmailHashFromStorage() ?? ''
      ldClient
        ?.identify({
          kind: 'multi',
          creditApplicant: {
            key: emailHash,
            email,
            _meta: {
              privateAttributes: ['/email'],
            },
          },
          [failedGoodFitFlowExperimentFlagName]: {
            key: emailHash,
          },
        })
        .then((flags) => {
          const experimentFlagValue = flags?.[failedGoodFitFlowExperimentFlagName]

          if (!experimentFlagValue || typeof experimentFlagValue !== 'object') {
            navigateToNotGoodFit()
            return
          }

          trackExperiment(failedGoodFitFlowExperimentFlagName, email ?? '', experimentFlagValue, 'email')

          if (
            experimentFlagValue.placement !== 'QUALIFIED' ||
            experimentFlagValue.behaviorName !== 'shownGoodFitPage'
          ) {
            navigateToNotGoodFit()
            return
          }

          navigateToGoodFit()
        })
        .catch(() => {
          navigateToNotGoodFit()
        })
    },
    onError: (error) => {
      logError(error, {
        attributes: {
          action: 'screenForCreditApplication',
          message: error.message,
          result: 'Error',
        },
        eventName: 'screenForCreditApplicationError',
      })

      track('se_signup__error_message__fired', {
        error_field: 'screenForCreditApplication',
        error_message_text: error.message,
      })
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='An error occurred while screening for credit application.'
          id='sputnik.GoodFitSurveyPage__Vcjh4E'
        />,
        { className: 'dd-privacy-allow' },
      )
      reset()
    },
  })

  const onSubmitGoodFitSurvey = (formValues) => {
    const { annualRevenue, currentCash, creditRange } = formValues

    screenForCreditApplication({
      variables: {
        screeningDecisionInput: {
          applicantEmail: (email as string) || '',
          annualRevenue,
          currentCash,
          creditRange,
        },
      },
    })
  }

  return (
    <>
      <BillBrandLogo />
      <FormProvider {...methods}>
        <form
          className='dd-privacy-allow'
          data-testid={makeTestId('form')}
          id={makeTestId('form')}
          onSubmit={handleSubmit(onSubmitGoodFitSurvey)}
        >
          <GoodFitWizardForm isScreeningForCreditApplication={isScreeningForCreditApplication} />
        </form>
      </FormProvider>
    </>
  )
}

export default GoodFitSurveyPage
